import type {
  ChartDataSource,
  CompositeQueryDefinition,
  CompositeQuerySource,
} from "./chartDataSource";

export type DataBindingOwnerType = "user" | "project" | "organization";
export type QueryTemplateDataViewOwnerType = "user" | "organization";
export type DataViewKind = "saved-view" | "query-template" | "federated-view";
export type CatalogDataViewCandidateKind = "query-template" | "federated-view";
export type CatalogSourceOwnerType = DataBindingOwnerType;
export type CatalogSourceKind = "schema";
export type CatalogSourceResolveOperation =
  | "read"
  | "create"
  | "write"
  | "update"
  | "delete"
  | "read-write";
export type ComponentDataWriteOperation = "create" | "update" | "delete";
export type CatalogSourceReconnectReason =
  | "missing-source-scope"
  | "source-inaccessible"
  | "source-owner-scope-mismatch"
  | "source-permission-denied"
  | "source-write-authority-required"
  | "source-unavailable";
export type FederatedViewPermissionMode = "read-only" | "read-write";
export type FederatedViewVisibility =
  | "private"
  | "project"
  | "organization"
  | "shared";
export type DataBindingFieldMapping = Record<string, string | undefined>;
export const COMPONENT_DATA_TEMPLATE_MAX_SOURCES = 8;

export interface DataSourceRef {
  kind: "schema";
  dataOwnerOrganizationId: string;
  ownerType: DataBindingOwnerType;
  ownerId: string;
  groupId: string;
  schemaId: string;
}

export type CatalogDataSourceRef = DataSourceRef;

export type CatalogSourceResolveSourceRef = CatalogDataSourceRef;

export interface CatalogSourceResolveRequest {
  source: CatalogSourceResolveSourceRef;
  operation?: CatalogSourceResolveOperation;
}

export interface CatalogSourceCandidate {
  sourceKind: CatalogSourceKind;
  authorizationPath?: string;
  source: CatalogDataSourceRef;
  labels: CatalogSourceLabels;
  capabilities: CatalogSourceCapabilities;
}

export interface CatalogSourceCandidateListResponse {
  candidates: CatalogSourceCandidate[];
}

export type CatalogDataViewRef =
  | QueryTemplateDataViewRef
  | FederatedDataViewRef;

export interface CatalogDataViewCandidate {
  viewKind: CatalogDataViewCandidateKind;
  view: CatalogDataViewRef;
  labels: CatalogDataViewLabels;
}

export interface CatalogDataViewCandidateListResponse {
  candidates: CatalogDataViewCandidate[];
}

export type DataCatalogSelection =
  | {
      selectionType: "source";
      source: CatalogDataSourceRef;
      labels: CatalogSourceLabels;
      capabilities: CatalogSourceCapabilities;
      writeTarget?: CatalogDataSourceRef;
    }
  | {
      selectionType: "view";
      view: CatalogDataViewRef;
      labels: CatalogDataViewLabels;
    };

export interface CatalogDataViewLabels {
  name: string;
  description?: string;
  ownerName?: string;
  viewOrganizationName?: string;
}

export interface CatalogSourceLabels {
  groupName: string;
  groupSlug: string;
  schemaName: string;
  schemaDisplayName: string;
}

export interface CatalogSourceFieldConstraints {
  min?: number;
  max?: number;
  precision?: number;
  minLength?: number;
  maxLength?: number;
  enum?: string[];
}

export interface CatalogSourceFieldMetadata {
  path: string;
  label: string;
  dataType: string;
  required: boolean;
  description?: string;
  constraints?: CatalogSourceFieldConstraints;
}

export interface CatalogSourceCapabilities {
  readable: boolean;
  writable: boolean;
}

export interface CatalogSourceQueryContext {
  dataOwnerOrganizationId: string;
  requestingOrganizationId: string | null;
}

export interface CatalogSourceError {
  code: CatalogSourceReconnectReason;
  message: string;
}

export interface CatalogSourceDiagnostics {
  errorCode: CatalogSourceReconnectReason | null;
  reconnectReason: CatalogSourceReconnectReason | null;
  sourceLabels: CatalogSourceLabels;
}

export interface ResolvedCatalogSource {
  source: CatalogDataSourceRef;
  queryContext: CatalogSourceQueryContext;
  labels: CatalogSourceLabels;
  fields: CatalogSourceFieldMetadata[];
  capabilities: CatalogSourceCapabilities;
  error: CatalogSourceError | null;
  reconnectReason: CatalogSourceReconnectReason | null;
  diagnostics: CatalogSourceDiagnostics;
}

export type CatalogSourceResolveResponse = ResolvedCatalogSource;

/**
 * Saved-view/catalog resolver contract:
 * `kind` selects the execution path, while owner and view scope are validated
 * server-side by data-service or by a backend facade that delegates there.
 */
interface ExecutableDataViewScope {
  ownerType: DataBindingOwnerType;
  ownerId: string;
  viewOrganizationId: string;
}

export interface SavedSchemaDataViewRef extends ExecutableDataViewScope {
  kind: "saved-view";
  viewId: string;
}

export interface QueryTemplateDataViewRef extends ExecutableDataViewScope {
  kind: "query-template";
  ownerType: QueryTemplateDataViewOwnerType;
  viewId: string;
}

export interface FederatedDataViewRef extends ExecutableDataViewScope {
  kind: "federated-view";
  viewId: string;
}

export interface StaticDataBindingRef {
  kind: "static";
  adapterId: string;
}

export type DataViewRef =
  | SavedSchemaDataViewRef
  | QueryTemplateDataViewRef
  | FederatedDataViewRef;

export type ComponentDataBindingSource =
  | StaticDataBindingRef
  | DataSourceRef
  | DataViewRef;

export interface ComponentDataBinding {
  source: ComponentDataBindingSource;
  fieldMapping: DataBindingFieldMapping;
  writeTarget?: ComponentDataBindingWriteTarget;
}

export type ComponentDataTemplateCreateRequest =
  | {
      templateSource: "catalog-source";
      requestingOrganizationId: string;
      source: DataSourceRef;
      name?: string;
    }
  | {
      templateSource: "catalog-sources";
      requestingOrganizationId: string;
      sources: DataSourceRef[];
      name?: string;
    }
  | {
      templateSource: "query-template-view";
      requestingOrganizationId: string;
      view: QueryTemplateDataViewRef;
      name?: string;
    }
  | {
      templateSource: "federated-view";
      requestingOrganizationId: string;
      view: FederatedDataViewRef;
      name?: string;
    };

export type ComponentDataTemplateExecutableSource =
  | DataSourceRef
  | QueryTemplateDataViewRef
  | FederatedDataViewRef;

export interface ComponentDataTemplateExecutableCreateResponse {
  templateId: string;
  name: string;
  executable: true;
  source?: ComponentDataTemplateExecutableSource;
  sources?: DataSourceRef[];
  sourceRefs?: DataSourceRef[];
  view?: FederatedDataViewRef;
  dataViewRef?: QueryTemplateDataViewRef | FederatedDataViewRef;
  dataBinding?: ComponentDataBinding;
}

export interface ComponentDataTemplateNeedsReviewResponse {
  templateId?: string;
  name: string;
  executable: false;
  status: "needs-review";
  reviewReason?: string;
  source?: ComponentDataTemplateExecutableSource;
  sources?: DataSourceRef[];
  sourceRefs?: DataSourceRef[];
  view?: FederatedDataViewRef;
  dataViewRef?: QueryTemplateDataViewRef | FederatedDataViewRef;
  dataBinding?: ComponentDataBinding;
}

export type ComponentDataTemplateCreateResponse =
  | ComponentDataTemplateExecutableCreateResponse
  | ComponentDataTemplateNeedsReviewResponse;

export interface FederatedViewSource {
  sourceId: string;
  source: DataSourceRef;
  permissionMode: FederatedViewPermissionMode;
  fieldMapping: DataBindingFieldMapping;
}

export interface FederatedViewSourceError {
  sourceId: string;
  reconnectReason: CatalogSourceReconnectReason;
  errorCode: CatalogSourceReconnectReason | null;
  message: string | null;
}

export interface FederatedViewSourceWriteTarget {
  kind: "federated-view-source";
  view: FederatedDataViewRef;
  targetSourceId: string;
  source: DataSourceRef;
}

export type ComponentDataBindingWriteTarget =
  | DataSourceRef
  | FederatedViewSourceWriteTarget;

export interface FederatedViewDefinition {
  viewId: string;
  ownerType: DataBindingOwnerType;
  ownerId: string;
  visibility: FederatedViewVisibility;
  sources: FederatedViewSource[];
}

const LEGACY_CHART_DATA_SOURCE_TYPES = new Set<string>([
  "static",
  "query-template",
  "timestamp-aggregate",
  "workflow",
  "composite-query",
  "home-assistant-placeholder",
]);

const LEGACY_CHART_AGGREGATIONS = new Set<string>([
  "sum",
  "avg",
  "min",
  "max",
  "count",
  "last",
  "first",
  "none",
]);

const LEGACY_COMPOSITE_JOIN_TYPES = new Set<string>(["inner", "left", "outer"]);

const LEGACY_COMPOSITE_JOIN_KEY_KINDS = new Set<string>([
  "exact",
  "time-bucket",
  "nearest-timestamp",
]);

const LEGACY_TIME_GRANULARITIES = new Set<string>([
  "minute",
  "hour",
  "day",
  "week",
  "month",
]);
const FORBIDDEN_FIELD_MAPPING_KEYS = new Set([
  "__proto__",
  "constructor",
  "prototype",
]);

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isLegacyChartAggregation(value: unknown): boolean {
  return typeof value === "string" && LEGACY_CHART_AGGREGATIONS.has(value);
}

function isLegacyTimeGranularity(value: unknown): boolean {
  return typeof value === "string" && LEGACY_TIME_GRANULARITIES.has(value);
}

function isLegacyChartMeasure(value: unknown): boolean {
  return (
    isRecord(value) &&
    isNonEmptyString(value.field) &&
    isLegacyChartAggregation(value.aggregation)
  );
}

function isLegacyChartQueryDefinition(value: unknown): boolean {
  return (
    isRecord(value) &&
    isNonEmptyString(value.schemaId) &&
    isNonEmptyString(value.groupId) &&
    isNonEmptyString(value.schemaName) &&
    Array.isArray(value.measures) &&
    value.measures.every(isLegacyChartMeasure)
  );
}

function hasLegacyQueryDefinition(value: Record<string, unknown>): boolean {
  return isLegacyChartQueryDefinition(value.queryDefinition);
}

function hasLegacyWorkflowConfig(value: Record<string, unknown>): boolean {
  const workflowConfig = value.workflowConfig;
  return (
    isRecord(workflowConfig) && isNonEmptyString(workflowConfig.workflowId)
  );
}

function isLegacyCompositeQuerySource(value: unknown): value is { id: string } {
  return (
    isRecord(value) &&
    isNonEmptyString(value.id) &&
    isLegacyChartQueryDefinition(value.query)
  );
}

export function isCompositeQuerySourceWithExplicitOwner(
  value: unknown,
): value is CompositeQuerySource & { dataOwnerOrganizationId: string } {
  return (
    isRecord(value) &&
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.dataOwnerOrganizationId) &&
    isLegacyChartQueryDefinition(value.query)
  );
}

function getCompositeSourceIds(value: unknown): Set<string> | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const sourceIds = new Set<string>();
  for (const source of value) {
    if (!isLegacyCompositeQuerySource(source) || sourceIds.has(source.id)) {
      return undefined;
    }
    sourceIds.add(source.id);
  }

  return sourceIds;
}

function getExplicitCompositeSourceIds(
  value: unknown,
): Set<string> | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const sourceIds = new Set<string>();
  for (const source of value) {
    if (
      !isCompositeQuerySourceWithExplicitOwner(source) ||
      sourceIds.has(source.id)
    ) {
      return undefined;
    }
    sourceIds.add(source.id);
  }

  return sourceIds;
}

function isLegacyCompositeJoinType(value: unknown): boolean {
  return typeof value === "string" && LEGACY_COMPOSITE_JOIN_TYPES.has(value);
}

function isLegacyCompositeJoinKey(value: unknown): boolean {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.left) ||
    !isNonEmptyString(value.right) ||
    (value.granularity !== undefined &&
      !isLegacyTimeGranularity(value.granularity))
  ) {
    return false;
  }

  if (value.kind === undefined) {
    return true;
  }

  if (
    typeof value.kind !== "string" ||
    !LEGACY_COMPOSITE_JOIN_KEY_KINDS.has(value.kind)
  ) {
    return false;
  }

  if (value.kind !== "nearest-timestamp") {
    return true;
  }

  return (
    typeof value.toleranceMs === "number" &&
    Number.isFinite(value.toleranceMs) &&
    value.toleranceMs > 0
  );
}

function isLegacyCompositeFieldMapping(
  value: unknown,
  sourceIds: Set<string>,
): boolean {
  return (
    isRecord(value) &&
    isNonEmptyString(value.sourceId) &&
    sourceIds.has(value.sourceId) &&
    isNonEmptyString(value.field) &&
    isNonEmptyString(value.alias)
  );
}

function hasLegacyCompositeQueryDefinition(
  value: Record<string, unknown>,
): boolean {
  const compositeQueryDefinition = value.compositeQueryDefinition;
  if (!isRecord(compositeQueryDefinition)) {
    return false;
  }

  const sourceIds = getCompositeSourceIds(compositeQueryDefinition.sources);
  const primarySourceId = compositeQueryDefinition.primarySourceId;
  return (
    sourceIds !== undefined &&
    sourceIds.size > 0 &&
    isNonEmptyString(primarySourceId) &&
    sourceIds.has(primarySourceId) &&
    isLegacyCompositeJoinType(compositeQueryDefinition.joinType) &&
    Array.isArray(compositeQueryDefinition.joinKeys) &&
    compositeQueryDefinition.joinKeys.every(isLegacyCompositeJoinKey) &&
    Array.isArray(compositeQueryDefinition.fields) &&
    compositeQueryDefinition.fields.every((field) =>
      isLegacyCompositeFieldMapping(field, sourceIds),
    )
  );
}

export function isCompositeQueryDefinitionWithExplicitSourceOwners(
  value: unknown,
): value is CompositeQueryDefinition {
  if (!isRecord(value)) {
    return false;
  }

  const sourceIds = getExplicitCompositeSourceIds(value.sources);
  const primarySourceId = value.primarySourceId;
  return (
    sourceIds !== undefined &&
    sourceIds.size > 0 &&
    isNonEmptyString(primarySourceId) &&
    sourceIds.has(primarySourceId) &&
    isLegacyCompositeJoinType(value.joinType) &&
    Array.isArray(value.joinKeys) &&
    value.joinKeys.every(isLegacyCompositeJoinKey) &&
    Array.isArray(value.fields) &&
    value.fields.every((field) =>
      isLegacyCompositeFieldMapping(field, sourceIds),
    )
  );
}

export function parseCompositeQueryDefinitionWithExplicitSourceOwners(
  value: unknown,
): CompositeQueryDefinition | undefined {
  return isCompositeQueryDefinitionWithExplicitSourceOwners(value)
    ? value
    : undefined;
}

function hasHomeAssistantPlaceholderShape(
  value: Record<string, unknown>,
): boolean {
  return (
    isNonEmptyString(value.entityId) &&
    (value.mode === "placeholder" ||
      value.execution === "none" ||
      value.manualOnly === true ||
      isNonEmptyString(value.attribute))
  );
}

function isDataBindingOwnerType(value: unknown): value is DataBindingOwnerType {
  return value === "user" || value === "project" || value === "organization";
}

function isQueryTemplateDataViewOwnerType(
  value: unknown,
): value is QueryTemplateDataViewOwnerType {
  return value === "user" || value === "organization";
}

function isCatalogSourceResolveOperation(
  value: unknown,
): value is CatalogSourceResolveOperation {
  return (
    value === "read" ||
    value === "create" ||
    value === "write" ||
    value === "update" ||
    value === "delete" ||
    value === "read-write"
  );
}

function isCatalogSourceReconnectReason(
  value: unknown,
): value is CatalogSourceReconnectReason {
  return (
    value === "missing-source-scope" ||
    value === "source-inaccessible" ||
    value === "source-owner-scope-mismatch" ||
    value === "source-permission-denied" ||
    value === "source-write-authority-required" ||
    value === "source-unavailable"
  );
}

function isDataViewKind(value: unknown): value is DataViewKind {
  return (
    value === "saved-view" ||
    value === "query-template" ||
    value === "federated-view"
  );
}

function isCatalogDataViewCandidateKind(
  value: unknown,
): value is CatalogDataViewCandidateKind {
  return value === "query-template" || value === "federated-view";
}

function hasExecutableDataViewScope(
  value: unknown,
): value is ExecutableDataViewScope {
  return (
    isRecord(value) &&
    isDataBindingOwnerType(value.ownerType) &&
    isNonEmptyString(value.ownerId) &&
    isNonEmptyString(value.viewOrganizationId)
  );
}

function isFederatedViewPermissionMode(
  value: unknown,
): value is FederatedViewPermissionMode {
  return value === "read-only" || value === "read-write";
}

function isFederatedViewVisibility(
  value: unknown,
): value is FederatedViewVisibility {
  return (
    value === "private" ||
    value === "project" ||
    value === "organization" ||
    value === "shared"
  );
}

export function isDataBindingFieldMapping(
  value: unknown,
): value is DataBindingFieldMapping {
  return (
    isRecord(value) &&
    Object.keys(value).every((key) => !FORBIDDEN_FIELD_MAPPING_KEYS.has(key)) &&
    Object.values(value).every(
      (field) => typeof field === "string" || field === undefined,
    )
  );
}

export function isDataSourceRef(value: unknown): value is DataSourceRef {
  return (
    isRecord(value) &&
    value.kind === "schema" &&
    isNonEmptyString(value.dataOwnerOrganizationId) &&
    isDataBindingOwnerType(value.ownerType) &&
    isNonEmptyString(value.ownerId) &&
    isNonEmptyString(value.groupId) &&
    isNonEmptyString(value.schemaId)
  );
}

export function parseDataSourceRef(value: unknown): DataSourceRef | undefined {
  return isDataSourceRef(value) ? value : undefined;
}

function isSameDataSourceRef(
  left: DataSourceRef,
  right: DataSourceRef,
): boolean {
  return (
    left.kind === right.kind &&
    left.dataOwnerOrganizationId === right.dataOwnerOrganizationId &&
    left.ownerType === right.ownerType &&
    left.ownerId === right.ownerId &&
    left.groupId === right.groupId &&
    left.schemaId === right.schemaId
  );
}

function isNonEmptyDataSourceRefArray(
  value: unknown,
): value is DataSourceRef[] {
  return (
    Array.isArray(value) && value.length > 0 && value.every(isDataSourceRef)
  );
}

function hasMultipleDataSourceRefs(value: unknown): value is DataSourceRef[] {
  return (
    isNonEmptyDataSourceRefArray(value) &&
    value.length > 1 &&
    value.length <= COMPONENT_DATA_TEMPLATE_MAX_SOURCES
  );
}

export function isCatalogSourceResolveSourceRef(
  value: unknown,
): value is CatalogSourceResolveSourceRef {
  return isDataSourceRef(value);
}

export function isCatalogSourceResolveRequest(
  value: unknown,
): value is CatalogSourceResolveRequest {
  return (
    isRecord(value) &&
    isCatalogSourceResolveSourceRef(value.source) &&
    (value.operation === undefined ||
      isCatalogSourceResolveOperation(value.operation))
  );
}

export function parseCatalogSourceResolveRequest(
  value: unknown,
): CatalogSourceResolveRequest | undefined {
  return isCatalogSourceResolveRequest(value) ? value : undefined;
}

function isCatalogSourceKind(value: unknown): value is CatalogSourceKind {
  return value === "schema";
}

export function isCatalogSourceLabels(
  value: unknown,
): value is CatalogSourceLabels {
  return (
    isRecord(value) &&
    isNonEmptyString(value.groupName) &&
    isNonEmptyString(value.groupSlug) &&
    isNonEmptyString(value.schemaName) &&
    isNonEmptyString(value.schemaDisplayName)
  );
}

export function isCatalogSourceFieldConstraints(
  value: unknown,
): value is CatalogSourceFieldConstraints {
  return (
    isRecord(value) &&
    (value.min === undefined || typeof value.min === "number") &&
    (value.max === undefined || typeof value.max === "number") &&
    (value.precision === undefined || typeof value.precision === "number") &&
    (value.minLength === undefined || typeof value.minLength === "number") &&
    (value.maxLength === undefined || typeof value.maxLength === "number") &&
    (value.enum === undefined ||
      (Array.isArray(value.enum) &&
        value.enum.every((option) => typeof option === "string")))
  );
}

export function isCatalogSourceFieldMetadata(
  value: unknown,
): value is CatalogSourceFieldMetadata {
  return (
    isRecord(value) &&
    isNonEmptyString(value.path) &&
    isNonEmptyString(value.label) &&
    isNonEmptyString(value.dataType) &&
    typeof value.required === "boolean" &&
    (value.description === undefined || isNonEmptyString(value.description)) &&
    (value.constraints === undefined ||
      isCatalogSourceFieldConstraints(value.constraints))
  );
}

export function isCatalogSourceCapabilities(
  value: unknown,
): value is CatalogSourceCapabilities {
  return (
    isRecord(value) &&
    typeof value.readable === "boolean" &&
    typeof value.writable === "boolean"
  );
}

const SUPPORTED_NON_ORG_CATALOG_SOURCE_KINDS = new Set<CatalogSourceKind>();

function hasCatalogCandidateAuthorizationPath(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

function hasCatalogSourceCandidateShape(
  value: unknown,
): value is CatalogSourceCandidate {
  return (
    isRecord(value) &&
    isCatalogSourceKind(value.sourceKind) &&
    isDataSourceRef(value.source) &&
    isCatalogSourceLabels(value.labels) &&
    isCatalogSourceCapabilities(value.capabilities)
  );
}

function hasSupportedNonOrgCatalogAuthority(
  candidate: CatalogSourceCandidate,
): boolean {
  return (
    candidate.source.ownerType !== "organization" &&
    hasCatalogCandidateAuthorizationPath(candidate.authorizationPath) &&
    SUPPORTED_NON_ORG_CATALOG_SOURCE_KINDS.has(candidate.sourceKind)
  );
}

export function isSelectableCatalogSourceCandidate(
  candidate: CatalogSourceCandidate,
): boolean {
  return (
    (candidate.sourceKind === "schema" &&
      candidate.source.ownerType === "organization") ||
    hasSupportedNonOrgCatalogAuthority(candidate)
  );
}

export function isCatalogSourceCandidate(
  value: unknown,
): value is CatalogSourceCandidate {
  if (!hasCatalogSourceCandidateShape(value)) {
    return false;
  }

  return isSelectableCatalogSourceCandidate(value);
}

export function parseCatalogSourceCandidate(
  value: unknown,
): CatalogSourceCandidate | undefined {
  return isCatalogSourceCandidate(value) ? value : undefined;
}

export function isCatalogSourceCandidateListResponse(
  value: unknown,
): value is CatalogSourceCandidateListResponse {
  return (
    isRecord(value) &&
    Array.isArray(value.candidates) &&
    value.candidates.every(isCatalogSourceCandidate)
  );
}

export function parseCatalogSourceCandidateListResponse(
  value: unknown,
): CatalogSourceCandidateListResponse | undefined {
  if (!isRecord(value) || !Array.isArray(value.candidates)) {
    return undefined;
  }

  return {
    candidates: value.candidates.flatMap((candidate) => {
      const parsed = parseCatalogSourceCandidate(candidate);
      return parsed === undefined ? [] : [parsed];
    }),
  };
}

export function isCatalogDataViewLabels(
  value: unknown,
): value is CatalogDataViewLabels {
  return (
    isRecord(value) &&
    isNonEmptyString(value.name) &&
    (value.description === undefined || isNonEmptyString(value.description)) &&
    (value.ownerName === undefined || isNonEmptyString(value.ownerName)) &&
    (value.viewOrganizationName === undefined ||
      isNonEmptyString(value.viewOrganizationName))
  );
}

function parseCatalogDataViewRef(
  value: unknown,
): CatalogDataViewRef | undefined {
  const view = parseDataViewRef(value);
  if (view?.kind === "query-template" && view.ownerType === "user") {
    return view;
  }
  if (view?.kind === "federated-view") {
    return view;
  }

  return undefined;
}

export function isCatalogDataViewCandidate(
  value: unknown,
): value is CatalogDataViewCandidate {
  if (
    !isRecord(value) ||
    !isCatalogDataViewCandidateKind(value.viewKind) ||
    !isCatalogDataViewLabels(value.labels)
  ) {
    return false;
  }

  const view = parseCatalogDataViewRef(value.view);
  return view !== undefined && view.kind === value.viewKind;
}

export function parseCatalogDataViewCandidate(
  value: unknown,
): CatalogDataViewCandidate | undefined {
  return isCatalogDataViewCandidate(value) ? value : undefined;
}

export function isCatalogDataViewCandidateListResponse(
  value: unknown,
): value is CatalogDataViewCandidateListResponse {
  return (
    isRecord(value) &&
    Array.isArray(value.candidates) &&
    value.candidates.every(isCatalogDataViewCandidate)
  );
}

export function parseCatalogDataViewCandidateListResponse(
  value: unknown,
): CatalogDataViewCandidateListResponse | undefined {
  if (!isRecord(value) || !Array.isArray(value.candidates)) {
    return undefined;
  }

  return {
    candidates: value.candidates.flatMap((candidate) => {
      const parsed = parseCatalogDataViewCandidate(candidate);
      return parsed === undefined ? [] : [parsed];
    }),
  };
}

export function isCatalogSourceQueryContext(
  value: unknown,
): value is CatalogSourceQueryContext {
  return (
    isRecord(value) &&
    isNonEmptyString(value.dataOwnerOrganizationId) &&
    (value.requestingOrganizationId === null ||
      isNonEmptyString(value.requestingOrganizationId))
  );
}

export function isCatalogSourceResolutionError(
  value: unknown,
): value is CatalogSourceError {
  return (
    isRecord(value) &&
    isCatalogSourceReconnectReason(value.code) &&
    isNonEmptyString(value.message)
  );
}

export function isCatalogSourceDiagnostics(
  value: unknown,
): value is CatalogSourceDiagnostics {
  return (
    isRecord(value) &&
    (value.errorCode === null ||
      isCatalogSourceReconnectReason(value.errorCode)) &&
    (value.reconnectReason === null ||
      isCatalogSourceReconnectReason(value.reconnectReason)) &&
    isCatalogSourceLabels(value.sourceLabels)
  );
}

export function isResolvedCatalogSource(
  value: unknown,
): value is ResolvedCatalogSource {
  return (
    isRecord(value) &&
    isDataSourceRef(value.source) &&
    isCatalogSourceQueryContext(value.queryContext) &&
    isCatalogSourceLabels(value.labels) &&
    Array.isArray(value.fields) &&
    value.fields.every(isCatalogSourceFieldMetadata) &&
    isCatalogSourceCapabilities(value.capabilities) &&
    (value.error === null || isCatalogSourceResolutionError(value.error)) &&
    (value.reconnectReason === null ||
      isCatalogSourceReconnectReason(value.reconnectReason)) &&
    isCatalogSourceDiagnostics(value.diagnostics)
  );
}

export function isCatalogSourceResolveResponse(
  value: unknown,
): value is CatalogSourceResolveResponse {
  return isResolvedCatalogSource(value);
}

export function parseCatalogSourceResolveResponse(
  value: unknown,
): CatalogSourceResolveResponse | undefined {
  return isCatalogSourceResolveResponse(value) ? value : undefined;
}

export function isDataViewRef(value: unknown): value is DataViewRef {
  if (
    !isRecord(value) ||
    !isDataViewKind(value.kind) ||
    !isNonEmptyString(value.viewId)
  ) {
    return false;
  }

  if (!hasExecutableDataViewScope(value)) {
    return false;
  }

  if (value.kind === "query-template") {
    return isQueryTemplateDataViewOwnerType(value.ownerType);
  }

  return true;
}

export function parseDataViewRef(value: unknown): DataViewRef | undefined {
  return isDataViewRef(value) ? value : undefined;
}

function isQueryTemplateDataViewRef(
  value: unknown,
): value is QueryTemplateDataViewRef {
  const view = parseDataViewRef(value);
  return view?.kind === "query-template";
}

function isFederatedDataViewRef(value: unknown): value is FederatedDataViewRef {
  const view = parseDataViewRef(value);
  return view?.kind === "federated-view";
}

function isSameDataViewRef(left: DataViewRef, right: DataViewRef): boolean {
  return (
    left.kind === right.kind &&
    left.viewId === right.viewId &&
    left.ownerType === right.ownerType &&
    left.ownerId === right.ownerId &&
    left.viewOrganizationId === right.viewOrganizationId
  );
}

export function isStaticDataBindingRef(
  value: unknown,
): value is StaticDataBindingRef {
  return (
    isRecord(value) &&
    value.kind === "static" &&
    isNonEmptyString(value.adapterId)
  );
}

export function isDataSourceOrViewRef(
  value: unknown,
): value is DataSourceRef | DataViewRef {
  return isDataSourceRef(value) || isDataViewRef(value);
}

export function isComponentDataBindingSource(
  value: unknown,
): value is ComponentDataBindingSource {
  return isStaticDataBindingRef(value) || isDataSourceOrViewRef(value);
}

export function isFederatedViewSourceWriteTarget(
  value: unknown,
): value is FederatedViewSourceWriteTarget {
  return (
    isRecord(value) &&
    value.kind === "federated-view-source" &&
    isDataViewRef(value.view) &&
    value.view.kind === "federated-view" &&
    isNonEmptyString(value.targetSourceId) &&
    isDataSourceRef(value.source)
  );
}

export function isComponentDataBindingWriteTarget(
  value: unknown,
): value is ComponentDataBindingWriteTarget {
  return isDataSourceRef(value) || isFederatedViewSourceWriteTarget(value);
}

export function parseComponentDataBindingWriteTarget(
  value: unknown,
): ComponentDataBindingWriteTarget | undefined {
  return isComponentDataBindingWriteTarget(value) ? value : undefined;
}

export function isComponentDataBinding(
  value: unknown,
): value is ComponentDataBinding {
  return (
    isRecord(value) &&
    isComponentDataBindingSource(value.source) &&
    isDataBindingFieldMapping(value.fieldMapping) &&
    (value.writeTarget === undefined ||
      isComponentDataBindingWriteTarget(value.writeTarget))
  );
}

export function parseComponentDataBinding(
  value: unknown,
): ComponentDataBinding | undefined {
  return isComponentDataBinding(value) ? value : undefined;
}

export function isComponentDataTemplateCreateRequest(
  value: unknown,
): value is ComponentDataTemplateCreateRequest {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.requestingOrganizationId) ||
    (value.name !== undefined && !isNonEmptyString(value.name))
  ) {
    return false;
  }

  if (value.templateSource === "catalog-source") {
    return (
      isDataSourceRef(value.source) &&
      value.sources === undefined &&
      value.view === undefined
    );
  }

  if (value.templateSource === "catalog-sources") {
    return (
      hasMultipleDataSourceRefs(value.sources) &&
      value.source === undefined &&
      value.view === undefined
    );
  }

  if (value.templateSource === "query-template-view") {
    return (
      isQueryTemplateDataViewRef(value.view) &&
      value.source === undefined &&
      value.sources === undefined
    );
  }

  if (value.templateSource === "federated-view") {
    return (
      isFederatedDataViewRef(value.view) &&
      value.source === undefined &&
      value.sources === undefined
    );
  }

  return false;
}

export function parseComponentDataTemplateCreateRequest(
  value: unknown,
): ComponentDataTemplateCreateRequest | undefined {
  return isComponentDataTemplateCreateRequest(value) ? value : undefined;
}

function parseComponentDataTemplateExecutableSource(
  value: unknown,
): ComponentDataTemplateExecutableSource | undefined {
  const source = parseDataSourceRef(value);
  if (source !== undefined) {
    return source;
  }

  const view = parseDataViewRef(value);
  if (view?.kind === "query-template" || view?.kind === "federated-view") {
    return view;
  }

  return undefined;
}

function parseTemplateResponseDataViewRef(
  value: unknown,
): QueryTemplateDataViewRef | FederatedDataViewRef | undefined {
  const view = parseDataViewRef(value);
  if (view?.kind === "query-template" || view?.kind === "federated-view") {
    return view;
  }

  return undefined;
}

function dataBindingMatchesTemplateSource(
  source: ComponentDataTemplateExecutableSource,
  dataBinding: ComponentDataBinding,
): boolean {
  if (isDataSourceRef(source)) {
    return (
      isDataSourceRef(dataBinding.source) &&
      isSameDataSourceRef(source, dataBinding.source)
    );
  }

  return (
    isDataViewRef(dataBinding.source) &&
    isSameDataViewRef(source, dataBinding.source)
  );
}

function hasValidOptionalTemplateResponseScope(
  value: Record<string, unknown>,
): boolean {
  return (
    (value.sources === undefined ||
      isNonEmptyDataSourceRefArray(value.sources)) &&
    (value.sourceRefs === undefined ||
      isNonEmptyDataSourceRefArray(value.sourceRefs)) &&
    (value.source === undefined ||
      parseComponentDataTemplateExecutableSource(value.source) !== undefined) &&
    (value.view === undefined || isFederatedDataViewRef(value.view)) &&
    (value.dataViewRef === undefined ||
      parseTemplateResponseDataViewRef(value.dataViewRef) !== undefined)
  );
}

export function isComponentDataTemplateCreateResponse(
  value: unknown,
): value is ComponentDataTemplateCreateResponse {
  if (!isRecord(value) || !isNonEmptyString(value.name)) {
    return false;
  }

  if (value.executable === false) {
    return (
      value.status === "needs-review" &&
      (value.templateId === undefined || isNonEmptyString(value.templateId)) &&
      (value.reviewReason === undefined ||
        isNonEmptyString(value.reviewReason)) &&
      (value.dataBinding === undefined ||
        isComponentDataBinding(value.dataBinding)) &&
      hasValidOptionalTemplateResponseScope(value)
    );
  }

  if (
    !isNonEmptyString(value.templateId) ||
    value.executable !== true ||
    !hasValidOptionalTemplateResponseScope(value)
  ) {
    return false;
  }

  const source = parseComponentDataTemplateExecutableSource(value.source);
  if (source !== undefined) {
    return (
      isComponentDataBinding(value.dataBinding) &&
      dataBindingMatchesTemplateSource(source, value.dataBinding)
    );
  }

  return (
    hasMultipleDataSourceRefs(value.sources) &&
    value.dataBinding === undefined
  );
}

export function parseComponentDataTemplateCreateResponse(
  value: unknown,
): ComponentDataTemplateCreateResponse | undefined {
  return isComponentDataTemplateCreateResponse(value) ? value : undefined;
}

export function isFederatedViewSource(
  value: unknown,
): value is FederatedViewSource {
  return (
    isRecord(value) &&
    isNonEmptyString(value.sourceId) &&
    isDataSourceRef(value.source) &&
    isFederatedViewPermissionMode(value.permissionMode) &&
    isDataBindingFieldMapping(value.fieldMapping)
  );
}

function hasUniqueFederatedSourceIds(sources: FederatedViewSource[]): boolean {
  const sourceIds = new Set<string>();
  for (const source of sources) {
    if (sourceIds.has(source.sourceId)) {
      return false;
    }
    sourceIds.add(source.sourceId);
  }

  return true;
}

export function isFederatedViewDefinition(
  value: unknown,
): value is FederatedViewDefinition {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.viewId) ||
    !isDataBindingOwnerType(value.ownerType) ||
    !isNonEmptyString(value.ownerId) ||
    !isFederatedViewVisibility(value.visibility) ||
    !Array.isArray(value.sources)
  ) {
    return false;
  }

  const sources = value.sources;
  return (
    sources.every(isFederatedViewSource) && hasUniqueFederatedSourceIds(sources)
  );
}

export function parseFederatedViewDefinition(
  value: unknown,
): FederatedViewDefinition | undefined {
  return isFederatedViewDefinition(value) ? value : undefined;
}

export function parseFederatedViewSourceError(
  value: unknown,
): FederatedViewSourceError | undefined {
  if (!isRecord(value) || !isNonEmptyString(value.sourceId)) {
    return undefined;
  }

  const reconnectReason = isCatalogSourceReconnectReason(value.reconnectReason)
    ? value.reconnectReason
    : isCatalogSourceReconnectReason(value.errorCode)
      ? value.errorCode
      : null;
  if (reconnectReason === null) {
    return undefined;
  }

  const errorCode = isCatalogSourceReconnectReason(value.errorCode)
    ? value.errorCode
    : null;
  const message =
    typeof value.message === "string" && value.message.trim().length > 0
      ? value.message.trim()
      : null;

  return {
    sourceId: value.sourceId,
    reconnectReason,
    errorCode,
    message,
  };
}

export function parseFederatedViewSourceErrors(
  value: unknown,
): FederatedViewSourceError[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const sourceErrors: FederatedViewSourceError[] = [];
  for (const item of value) {
    const sourceError = parseFederatedViewSourceError(item);
    if (sourceError !== undefined) {
      sourceErrors.push(sourceError);
    }
  }

  return sourceErrors;
}

export function isLegacyChartDataSource(
  value: unknown,
): value is ChartDataSource {
  if (
    !isRecord(value) ||
    typeof value.type !== "string" ||
    !LEGACY_CHART_DATA_SOURCE_TYPES.has(value.type)
  ) {
    return false;
  }

  if (value.type === "static") {
    return true;
  }

  if (value.type === "query-template" || value.type === "timestamp-aggregate") {
    return hasLegacyQueryDefinition(value);
  }

  if (value.type === "workflow") {
    return hasLegacyWorkflowConfig(value);
  }

  if (value.type === "composite-query") {
    return hasLegacyCompositeQueryDefinition(value);
  }

  return hasHomeAssistantPlaceholderShape(value);
}

export function isRecordArray(
  value: unknown,
): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.every(isRecord);
}

export function getRecordArray(
  value: unknown,
): Record<string, unknown>[] | undefined {
  return isRecordArray(value) ? value : undefined;
}

export const isUnknownRecord = isRecord;
